import { useEffect, useState } from "react";
import { api, formatTZS, formatDateTime, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Eye, CheckCircle2, LogIn, LogOut, XCircle, Search as SearchIcon, Plus, Trash2, Download, Mail, BadgeDollarSign } from "lucide-react";

const statusColor = {
  booked: "bg-blue-100 text-blue-700",
  checked_in: "bg-emerald-100 text-emerald-700",
  checked_out: "bg-slate-100 text-slate-700",
  cancelled: "bg-red-100 text-red-700",
};

const SERVICE_CATS = [
  { value: "restaurant", label: "Restaurant" },
  { value: "laundry", label: "Laundry" },
  { value: "minibar", label: "Mini bar" },
  { value: "spa", label: "Spa" },
  { value: "other", label: "Other" },
];

export default function BookingsPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [service, setService] = useState([]);
  const [rsForm, setRsForm] = useState({ item: "", category: "restaurant", quantity: 1, amount: 0, notes: "" });
  const [emailing, setEmailing] = useState(false);

  const canManage = user?.role === "super_admin" || user?.role === "admin";

  const load = async () => {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/bookings?${params.toString()}`);
    setBookings(data);
  };
  useEffect(() => { load(); }, [filter]);

  const openBooking = async (b) => {
    setSelected(b);
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/room-service`);
      setService(data);
    } catch { setService([]); }
  };

  const confirmPayment = async (id) => {
    try { await api.post(`/admin/bookings/${id}/confirm-payment`); toast.success("Payment confirmed"); load();
      if (selected?.id === id) setSelected({ ...selected, payment_status: "paid" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doCheckout = async (id) => {
    try { await api.post(`/admin/bookings/${id}/checkout`); toast.success("Checked out"); load(); setSelected(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async (id) => {
    if (!confirm("Cancel this booking?")) return;
    try { await api.post(`/admin/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); setSelected(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addService = async () => {
    if (!rsForm.item || !rsForm.amount) return toast.error("Item and amount required");
    try {
      await api.post(`/admin/bookings/${selected.id}/room-service`, {
        item: rsForm.item, category: rsForm.category,
        quantity: Number(rsForm.quantity), amount: Number(rsForm.amount), notes: rsForm.notes,
      });
      toast.success("Charge added");
      const { data } = await api.get(`/admin/bookings/${selected.id}/room-service`);
      setService(data);
      setRsForm({ item: "", category: "restaurant", quantity: 1, amount: 0, notes: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const paySvc = async (sid) => {
    try { await api.post(`/admin/room-service/${sid}/pay`);
      const { data } = await api.get(`/admin/bookings/${selected.id}/room-service`);
      setService(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const settleAll = async () => {
    try {
      await api.post(`/admin/bookings/${selected.id}/room-service/settle-all`);
      const { data } = await api.get(`/admin/bookings/${selected.id}/room-service`);
      setService(data);
      toast.success("All settled");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delSvc = async (sid) => {
    try { await api.delete(`/admin/room-service/${sid}`);
      const { data } = await api.get(`/admin/bookings/${selected.id}/room-service`);
      setService(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const svcTotal = service.reduce((a, s) => a + (s.total || 0), 0);
  const svcUnpaid = service.filter((s) => !s.paid).reduce((a, s) => a + (s.total || 0), 0);

  const downloadInvoice = async (id, ref) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${id}/invoice.pdf`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("baraka_token") || ""}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `baraka-invoice-${ref || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Invoice downloaded");
    } catch (e) { toast.error(e.message); }
  };

  const emailInvoice = async (id) => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/invoice/email`);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div>
        <h1 className="font-heading text-3xl text-[color:var(--deep)]">Bookings</h1>
        <p className="text-sm text-[color:var(--muted-text)] mt-1">Review, confirm payments, log room service, check guests in and out.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--muted-text)]" />
          <Input placeholder="Search reference, name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="bookings-search" className="pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="bookings-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="checked_in">Checked in</SelectItem>
            <SelectItem value="checked_out">Checked out</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} data-testid="bookings-search-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">Search</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Type / Room</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.reference}`} className="hover:bg-[color:var(--sand)]">
                  <TableCell className="font-mono text-xs">{b.reference}</TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{b.guest_name}</p>
                    <p className="text-xs text-[color:var(--muted-text)]">{b.guest_phone}</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    <p>{b.room_type_name}</p>
                    {b.room_unit_number && <p className="text-xs text-[color:var(--brand)]">Room {b.room_unit_number}</p>}
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(b.check_in_date)}<br />→ {formatDate(b.check_out_date)}</TableCell>
                  <TableCell className="font-semibold text-sm">{formatTZS(b.total_amount)}</TableCell>
                  <TableCell><Badge className={b.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{b.payment_status}</Badge></TableCell>
                  <TableCell><Badge className={`${statusColor[b.status] || ""} hover:opacity-90`}>{b.status.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-xs">
                    {b.checked_in_by && <p className="text-emerald-700" title={`Checked in by ${b.checked_in_by}`}>↓ {b.checked_in_by.split("@")[0]}</p>}
                    {b.checked_out_by && <p className="text-slate-600" title={`Checked out by ${b.checked_out_by}`}>↑ {b.checked_out_by.split("@")[0]}</p>}
                    {!b.checked_in_by && !b.checked_out_by && <span className="text-[color:var(--muted-text)]">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {b.payment_status !== "paid" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); if (window.confirm(`Mark booking ${b.reference} as PAID?\nGuest will get a receipt email.`)) confirmPayment(b.id); }}
                        title="Mark paid"
                        data-testid={`row-mark-paid-${b.reference}`}
                        className="text-emerald-700 hover:bg-emerald-50 mr-1"
                      >
                        <BadgeDollarSign className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openBooking(b)} data-testid={`view-booking-${b.reference}`}><Eye className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {bookings.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-sm text-[color:var(--muted-text)] py-10">No bookings yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Booking {selected.reference}</DialogTitle>
                <DialogDescription>Full details, room service and staff actions.</DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details">
                <TabsList>
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                  <TabsTrigger value="service" data-testid="tab-service">Room service {svcUnpaid > 0 && <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">•</span>}</TabsTrigger>
                  {selected.checkin_details && <TabsTrigger value="checkin" data-testid="tab-checkin-details">Check-in</TabsTrigger>}
                </TabsList>

                <TabsContent value="details">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Field label="Guest" value={selected.guest_name} />
                    <Field label="Phone" value={selected.guest_phone} />
                    <Field label="Email" value={selected.guest_email} />
                    <Field label="Room type" value={selected.room_type_name} />
                    <Field label="Room #" value={selected.room_unit_number || "—"} />
                    <Field label="Dates" value={`${formatDate(selected.check_in_date)} → ${formatDate(selected.check_out_date)}`} />
                    <Field label="Nights" value={selected.nights} />
                    <Field label="Guests" value={`${selected.adults}A · ${selected.children}C`} />
                    <Field label="Amount" value={formatTZS(selected.total_amount)} />
                    <Field label="Payment" value={`${selected.payment_method} — ${selected.payment_status}`} />
                    <Field label="Status" value={selected.status} />
                    <Field label="Booked at" value={formatDateTime(selected.created_at)} />
                    {selected.notes && <Field label="Notes" value={selected.notes} colSpan />}
                  </div>

                  <div className="mt-6 p-4 rounded-xl border border-[color:var(--brand)]/15 bg-[color:var(--sand)]/40" data-testid="staff-activity">
                    <p className="text-xs uppercase tracking-wider text-[color:var(--brand)] font-semibold mb-3">Staff activity — who did what</p>
                    <ol className="space-y-3">
                      <TimelineItem
                        color="bg-blue-500"
                        title="Booking created"
                        when={selected.created_at}
                        who="Guest self-booking (public site)"
                      />
                      {selected.payment_confirmed_at && (
                        <TimelineItem
                          color="bg-emerald-500"
                          title="Payment confirmed"
                          when={selected.payment_confirmed_at}
                          who={selected.payment_confirmed_by}
                        />
                      )}
                      {selected.checked_in_at && (
                        <TimelineItem
                          color="bg-amber-500"
                          title={`Checked in${selected.room_unit_number ? ` — Room ${selected.room_unit_number}` : ""}`}
                          when={selected.checked_in_at}
                          who={selected.checked_in_by || selected.checkin_details?.checked_in_by}
                        />
                      )}
                      {selected.invoice_emailed_at && (
                        <TimelineItem
                          color="bg-indigo-500"
                          title={`Invoice emailed to ${selected.invoice_emailed_to || ""}`}
                          when={selected.invoice_emailed_at}
                          who={selected.invoice_emailed_by}
                        />
                      )}
                      {selected.checked_out_at && (
                        <TimelineItem
                          color="bg-slate-600"
                          title="Checked out"
                          when={selected.checked_out_at}
                          who={selected.checked_out_by}
                        />
                      )}
                      {selected.cancelled_at && (
                        <TimelineItem
                          color="bg-red-500"
                          title="Booking cancelled"
                          when={selected.cancelled_at}
                          who="—"
                        />
                      )}
                    </ol>
                  </div>
                </TabsContent>

                <TabsContent value="service">
                  <div className="space-y-4">
                    {selected.status === "checked_in" && (
                      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end p-3 rounded-lg bg-[color:var(--sand)]">
                        <div><Label className="text-xs">Item</Label><Input value={rsForm.item} onChange={(e) => setRsForm({ ...rsForm, item: e.target.value })} placeholder="e.g. Dinner for two" data-testid="rs-item" /></div>
                        <div><Label className="text-xs">Category</Label>
                          <Select value={rsForm.category} onValueChange={(v) => setRsForm({ ...rsForm, category: v })}>
                            <SelectTrigger data-testid="rs-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{SERVICE_CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={rsForm.quantity} onChange={(e) => setRsForm({ ...rsForm, quantity: e.target.value })} data-testid="rs-qty" /></div>
                        <div><Label className="text-xs">Unit price (TZS)</Label><Input type="number" min="0" value={rsForm.amount} onChange={(e) => setRsForm({ ...rsForm, amount: e.target.value })} data-testid="rs-amount" /></div>
                        <Button onClick={addService} className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]" data-testid="rs-add"><Plus className="w-4 h-4" /></Button>
                      </div>
                    )}

                    <div className="flex items-center justify-between px-1">
                      <p className="text-sm text-[color:var(--muted-text)]">Total: <span className="font-heading text-[color:var(--deep)]">{formatTZS(svcTotal)}</span> · Unpaid: <span className="text-red-600 font-medium">{formatTZS(svcUnpaid)}</span></p>
                      {svcUnpaid > 0 && <Button size="sm" variant="outline" onClick={settleAll} data-testid="rs-settle-all">Settle all</Button>}
                    </div>

                    <Table>
                      <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Cat</TableHead><TableHead>Qty</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                      <TableBody>
                        {service.map((s) => (
                          <TableRow key={s.id} data-testid={`rs-row-${s.id}`}>
                            <TableCell className="text-sm">{s.item}</TableCell>
                            <TableCell><Badge variant="outline">{s.category}</Badge></TableCell>
                            <TableCell className="text-sm">{s.quantity} × {formatTZS(s.amount)}</TableCell>
                            <TableCell className="font-medium">{formatTZS(s.total)}</TableCell>
                            <TableCell>{s.paid ? <Badge className="bg-emerald-100 text-emerald-700">paid</Badge> : <Badge className="bg-red-100 text-red-700">unpaid</Badge>}</TableCell>
                            <TableCell className="text-right space-x-1">
                              {!s.paid && <Button size="sm" variant="ghost" onClick={() => paySvc(s.id)} data-testid={`rs-pay-${s.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>}
                              {canManage && <Button size="sm" variant="ghost" onClick={() => delSvc(s.id)} className="text-red-600" data-testid={`rs-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                            </TableCell>
                          </TableRow>
                        ))}
                        {service.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm text-[color:var(--muted-text)]">No room service yet.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                {selected.checkin_details && (
                  <TabsContent value="checkin">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(selected.checkin_details).filter(([k]) => k !== "id_photo_path").map(([k, v]) => (
                        <Field key={k} label={k.replace(/_/g, " ")} value={String(v ?? "—")} />
                      ))}
                    </div>
                    {selected.checkin_details.id_photo_path && (
                      <div className="mt-4">
                        <p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">ID photo</p>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${selected.checkin_details.id_photo_path}`} alt="ID" className="mt-2 max-w-xs rounded-lg border" />
                      </div>
                    )}
                  </TabsContent>
                )}
              </Tabs>

              <DialogFooter className="flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadInvoice(selected.id, selected.reference)} data-testid="download-invoice-btn"><Download className="w-4 h-4 mr-1" /> Invoice PDF</Button>
                <Button size="sm" variant="outline" onClick={() => emailInvoice(selected.id)} disabled={emailing} data-testid="email-invoice-btn"><Mail className="w-4 h-4 mr-1" /> {emailing ? "Sending…" : "Email guest"}</Button>
                {selected.payment_status !== "paid" && (
                  <Button size="sm" onClick={() => confirmPayment(selected.id)} data-testid="confirm-payment-btn" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm payment</Button>
                )}
                {selected.status === "booked" && (
                  <Button size="sm" variant="outline" onClick={() => window.location.href = "/admin/checkin?ref=" + selected.reference} data-testid="go-checkin-btn"><LogIn className="w-4 h-4 mr-1" /> Check in</Button>
                )}
                {selected.status === "checked_in" && (
                  <Button size="sm" onClick={() => doCheckout(selected.id)} data-testid="checkout-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><LogOut className="w-4 h-4 mr-1" /> Check out</Button>
                )}
                {canManage && selected.status !== "cancelled" && selected.status !== "checked_out" && (
                  <Button size="sm" variant="destructive" onClick={() => cancel(selected.id)} data-testid="cancel-btn"><XCircle className="w-4 h-4 mr-1" /> Cancel</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, colSpan }) {
  return (
    <div className={colSpan ? "col-span-2" : ""}>
      <p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)]">{label}</p>
      <p className="mt-0.5 text-sm text-[color:var(--deep)]">{String(value ?? "—")}</p>
    </div>
  );
}

function TimelineItem({ color, title, when, who }) {
  return (
    <li className="flex gap-3" data-testid={`timeline-${title.split(" ")[0].toLowerCase()}`}>
      <div className="flex flex-col items-center pt-1">
        <span className={`w-2.5 h-2.5 rounded-full ${color} ring-2 ring-white shadow`} />
        <span className="flex-1 w-px bg-[color:var(--brand)]/20 mt-1" />
      </div>
      <div className="flex-1 pb-1">
        <p className="text-sm font-medium text-[color:var(--deep)]">{title}</p>
        <p className="text-xs text-[color:var(--muted-text)] mt-0.5">
          {when ? formatDateTime(when) : "—"}
          {who && <> · by <span className="font-medium text-[color:var(--brand)]">{who}</span></>}
        </p>
      </div>
    </li>
  );
}
